import {
  QueryFunctionContext,
  QueryKey,
  QueryMeta,
  useQuery,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

export type FetchQueryParams<TQueryResult, TData, TError = Error> = {
  queryKey: QueryKey;
  url: string;
  requestHeaders?: Record<string, string>;
  options?: Omit<UseQueryOptions<TQueryResult, TError, TData>, 'queryKey' | 'queryFn'>;
  method?: 'GET' | 'POST';
  body?: Record<string, unknown> | null;
  meta?: QueryMeta;
  enabled?: boolean;
  staleTime?: number;
};

export function doQueryOptions<TQueryResult, TData = TQueryResult, TError = Error>({
  queryKey,
  url,
  options = {},
  requestHeaders = {},
  method = 'GET',
  body = null,
  ...rest
}: FetchQueryParams<TQueryResult, TData, TError>): UseQueryOptions<TQueryResult, TError, TData> {
  const queryFn = async ({ signal }: QueryFunctionContext) => {
    const response = await fetch(url, {
      method,
      headers: {
        'X-REQUEST-ID': uuidv4(),
        'Content-Type': 'application/json',
        ...requestHeaders
      },
      body: method === 'POST' ? JSON.stringify(body) : null,
      signal
    });
    return response.json() as Promise<TQueryResult>;
  };
  return { queryKey, queryFn, ...rest, ...options };
}

export function useFetchQuery<TQueryResult, TData = TQueryResult, TError = Error>(
  params: FetchQueryParams<TQueryResult, TData, TError>
): UseQueryResult<TData, TError> {
  const queryOptions = doQueryOptions<TQueryResult, TData, TError>(params);
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}

export function usePostQuery<TQueryResult, TData = TQueryResult, TError = Error>(
  params: FetchQueryParams<TQueryResult, TData, TError>
): UseQueryResult<TData, TError> {
  const queryOptions = doQueryOptions<TQueryResult, TData, TError>({ ...params, method: 'POST' });
  return useQuery<TQueryResult, TError, TData>(queryOptions);
}
